/**
 * Native (non-iframe) landing page for the Docs tab hub — filter chips,
 * START HERE / CANONICAL callouts, grouped card sections, and a "More" bar
 * for raw files + external links. Replaces the old index.html iframe.
 */
import { useMemo, useState } from "react";
import { Box, Button } from "@mui/material";
import { Icon } from "../../../mui";
import { brainTokens } from "../../../design/brainTheme";
import type { StoreObject } from "../../../lib/brain-api";
import { isLinkKind, type ScopeIndex, type ScopeIndexEntry, type SectionGroup } from "./scopeIndexModel";

const c = brainTokens.color;

function linkIcon(kind: string): string {
  return kind === "jira" ? "clipboard-list" : "external-link";
}

/** START HERE / CANONICAL are inferred from the handoff.md / plan.md naming
 * convention used across every scope's docs — not a fabricated tag. */
function findCallout(groups: SectionGroup[], key: string): ScopeIndexEntry | null {
  for (const g of groups) {
    const hit = g.entries.find((e) => e.key === key);
    if (hit) return hit;
  }
  return null;
}

function Callout({
  kind,
  entry,
  onClick,
}: {
  kind: "start" | "canonical";
  entry: ScopeIndexEntry;
  onClick: () => void;
}) {
  const isStart = kind === "start";
  return (
    <Box
      onClick={onClick}
      sx={{
        background: isStart ? c.tealTint2 : c.white,
        border: `1px solid ${isStart ? c.tealTintBorder : c.teal}`,
        borderRadius: "10px",
        padding: "18px 20px",
        cursor: "pointer",
        "&:hover": { borderColor: isStart ? c.tealTintBorder : c.tealHover },
      }}
    >
      <Box sx={{ display: "flex", alignItems: "center", gap: "8px", mb: "10px" }}>
        {isStart ? (
          <Box
            component="span"
            sx={{
              fontFamily: brainTokens.font.mono,
              fontSize: 10,
              fontWeight: 600,
              letterSpacing: ".1em",
              color: c.tealInk,
            }}
          >
            START HERE
          </Box>
        ) : (
          <>
            <Box
              component="span"
              sx={{
                fontFamily: brainTokens.font.mono,
                fontSize: 10,
                fontWeight: 600,
                letterSpacing: ".08em",
                color: c.white,
                background: c.teal,
                borderRadius: "4px",
                padding: "2px 7px",
              }}
            >
              CANONICAL
            </Box>
            <Box component="span" sx={{ fontFamily: brainTokens.font.mono, fontSize: 10, color: c.faint }}>
              PLAN &amp; STATUS
            </Box>
          </>
        )}
      </Box>
      <Box sx={{ fontSize: 15, fontWeight: 600, color: c.tealInk, mb: "6px" }}>{entry.title}</Box>
      {entry.desc && (
        <Box sx={{ fontSize: 13, color: c.ink4, lineHeight: 1.55 }}>{entry.desc}</Box>
      )}
    </Box>
  );
}

function EntryCard({ entry, onClick }: { entry: ScopeIndexEntry; onClick: () => void }) {
  return (
    <Box
      onClick={onClick}
      sx={{
        background: c.white,
        border: `1px solid ${c.border}`,
        borderRadius: "8px",
        padding: "16px 18px",
        cursor: "pointer",
        transition: "border-color .15s",
        "&:hover": { borderColor: c.hoverBorder },
      }}
    >
      <Box sx={{ fontSize: 14.5, fontWeight: 600, color: c.tealInk, mb: "5px" }}>{entry.title}</Box>
      {entry.desc && (
        <Box
          sx={{
            fontSize: 13,
            color: c.muted,
            lineHeight: 1.55,
            display: "-webkit-box",
            WebkitLineClamp: 3,
            WebkitBoxOrient: "vertical",
            overflow: "hidden",
          }}
        >
          {entry.desc}
        </Box>
      )}
    </Box>
  );
}

export function ScopeLanding({
  index,
  groups,
  rawFiles,
  onOpen,
  onOpenLink,
  onOpenFiles,
}: {
  /** null → no summary lede. */
  index: ScopeIndex | null;
  /** From resolveSectionGroups — already bucketed + ordered. */
  groups: SectionGroup[];
  /** Raw (non-index) store files — feeds the "Files" chip + More bar count. */
  rawFiles: StoreObject[];
  /** doc/plan/data entries. */
  onOpen: (relKey: string) => void;
  /** jira/external/scope entries. */
  onOpenLink: (entry: ScopeIndexEntry) => void;
  /** "Files" chip / More-bar count → open the Manage files pane. */
  onOpenFiles?: () => void;
}) {
  const [filter, setFilter] = useState<string>("all");

  const cardGroups = useMemo(() => groups.filter((g) => g.section.layout !== "links"), [groups]);
  const linkEntries = useMemo(
    () => groups.filter((g) => g.section.layout === "links").flatMap((g) => g.entries),
    [groups],
  );

  const startHere = useMemo(() => findCallout(cardGroups, "handoff.md"), [cardGroups]);
  const canonical = useMemo(() => findCallout(cardGroups, "plan.md"), [cardGroups]);
  const calloutKeys = useMemo(
    () => new Set([startHere?.key, canonical?.key].filter(Boolean) as string[]),
    [startHere, canonical],
  );

  const totalEntries = cardGroups.reduce((n, g) => n + g.entries.length, 0);
  const chips = [
    { key: "all", label: "All", count: totalEntries },
    ...cardGroups.map((g) => ({ key: g.section.key, label: g.section.title, count: g.entries.length })),
    ...(rawFiles.length ? [{ key: "__files__", label: "Files", count: rawFiles.length }] : []),
  ];

  const visibleGroups = filter === "all" || filter === "__files__"
    ? cardGroups
    : cardGroups.filter((g) => g.section.key === filter);

  const handleEntryClick = (entry: ScopeIndexEntry) => {
    if (isLinkKind(entry.kind)) onOpenLink(entry);
    else onOpen(entry.key);
  };

  return (
    <Box>
      {index && (index.summary || index.scope) && (
        <Box sx={{ mb: "18px" }}>
          {index.summary && (
            <Box sx={{ fontSize: 16, color: c.ink2, lineHeight: 1.6, maxWidth: 720, mb: "4px" }}>{index.summary}</Box>
          )}
          <Box sx={{ fontSize: 12, color: c.faint }}>
            {index.scope} · updated {index.updatedAt}
          </Box>
        </Box>
      )}

      {/* Filter chips */}
      <Box sx={{ display: "flex", alignItems: "center", gap: "8px", flexWrap: "wrap" }}>
        {chips.map((chip) => {
          const active = filter === chip.key;
          return (
            <Box
              key={chip.key}
              component="button"
              onClick={() => (chip.key === "__files__" ? onOpenFiles?.() : setFilter(chip.key))}
              sx={{
                appearance: "none",
                border: `1px solid ${active ? "transparent" : c.border}`,
                background: active ? c.ink : c.white,
                color: active ? c.white : c.ink4,
                borderRadius: "999px",
                padding: active ? "5px 13px" : "4px 12px",
                fontSize: 12.5,
                fontWeight: active ? 600 : 400,
                fontFamily: "inherit",
                cursor: "pointer",
              }}
            >
              {chip.label} {chip.count}
            </Box>
          );
        })}
        <Box sx={{ flex: 1 }} />
      </Box>

      {(startHere || canonical) && (
        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: startHere && canonical ? "1fr 1fr" : "1fr",
            gap: "14px",
            mt: "16px",
          }}
        >
          {startHere && <Callout kind="start" entry={startHere} onClick={() => handleEntryClick(startHere)} />}
          {canonical && <Callout kind="canonical" entry={canonical} onClick={() => handleEntryClick(canonical)} />}
        </Box>
      )}

      {visibleGroups.map((group) => {
        const entries = group.entries.filter((e) => !calloutKeys.has(e.key));
        if (entries.length === 0) return null;
        return (
          <Box key={group.section.key} sx={{ mt: "26px" }}>
            <Box sx={{ display: "flex", alignItems: "center", gap: "12px", mb: "12px" }}>
              <Box
                component="span"
                sx={{
                  fontFamily: brainTokens.font.mono,
                  fontSize: 10,
                  fontWeight: 600,
                  letterSpacing: ".1em",
                  color: c.faint,
                  whiteSpace: "nowrap",
                }}
              >
                {group.section.title.toUpperCase()}
              </Box>
              <Box sx={{ flex: 1, height: "1px", background: c.border }} />
            </Box>
            <Box sx={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(220px, 1fr))", gap: "14px" }}>
              {entries.map((entry) => (
                <EntryCard key={entry.key} entry={entry} onClick={() => handleEntryClick(entry)} />
              ))}
            </Box>
          </Box>
        );
      })}

      {/* More bar — raw files + external links */}
      {(rawFiles.length > 0 || linkEntries.length > 0 || index) && (
        <Box
          sx={{
            mt: "26px",
            background: c.white,
            border: `1px solid ${c.border}`,
            borderRadius: "8px",
            padding: "13px 18px",
            display: "flex",
            alignItems: "center",
            gap: "14px",
            flexWrap: "wrap",
          }}
        >
          <Box
            component="span"
            sx={{ fontFamily: brainTokens.font.mono, fontSize: 10, fontWeight: 600, letterSpacing: ".1em", color: c.faint }}
          >
            MORE
          </Box>
          {rawFiles.length > 0 && (
            <Button
              size="small"
              variant="text"
              onClick={onOpenFiles}
              sx={{
                fontFamily: brainTokens.font.mono,
                fontSize: 11,
                color: c.ink3,
                background: c.panelBg2,
                borderRadius: "5px",
                padding: "2px 8px",
                minWidth: 0,
                textTransform: "none",
              }}
            >
              Files · {rawFiles.length}
            </Button>
          )}
          {linkEntries.length > 0 && (
            <>
              <Box sx={{ width: "1px", height: "18px", background: c.border }} />
              {linkEntries.map((entry) => (
                <Box
                  key={`${entry.kind}:${entry.key}`}
                  onClick={() => onOpenLink(entry)}
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    gap: "5px",
                    fontFamily: brainTokens.font.mono,
                    fontSize: 11,
                    color: c.citeText,
                    background: c.citeBg,
                    border: `1px solid ${c.citeBorder}`,
                    borderRadius: "5px",
                    padding: "2px 8px",
                    cursor: "pointer",
                    "&:hover": { borderColor: c.teal },
                  }}
                >
                  <Icon name={linkIcon(entry.kind)} size={11} />
                  {entry.title} ↗
                </Box>
              ))}
            </>
          )}
          {index && (
            <>
              <Box sx={{ flex: 1 }} />
              <Box component="span" sx={{ fontFamily: brainTokens.font.mono, fontSize: 11, color: c.muted2 }}>
                Updated {index.updatedAt}
              </Box>
            </>
          )}
        </Box>
      )}
    </Box>
  );
}
